import type { QmsLabelSize } from './types';

/**
 * A complete display-board palette derived from two layout colors.
 *
 * The board canvas is a fixed light neutral. The layout's `accent_color`
 * is the brand color and carries every number on the board — the Token
 * codes, the Counter codes, and the Waiting tokens. The layout's
 * `label_color` colors the section labels — the Token / Counter / Waiting
 * headings — independently; when it is unset it falls back to the accent,
 * so a board configured before this split looks unchanged. The layout's
 * `label_size` scales those same labels.
 */
export interface BoardTheme {
  /** Page background — a fixed light neutral. */
  background: string;
  /** Primary dark text. */
  foreground: string;
  /** Secondary / label text. */
  muted: string;
  /** Panel and row fill. */
  surface: string;
  /** Panel and row border. */
  border: string;
  /** Brand color — Token codes, Counter codes, Waiting tokens. */
  accent: string;
  /** Label color — the Token / Counter / Waiting section headings. */
  labelAccent: string;
  /** Font size (CSS length) for the section headings. */
  labelFontSize: string;
  /** Brand color at low opacity — soft fills and tinted borders. */
  accentSoft: string;
  /** Now-serving row fill — the solid brand color. */
  highlightBackground: string;
  /** Text on the now-serving row — contrast-picked against the accent. */
  highlightForeground: string;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Fixed light board canvas — #f8fafc. */
const BACKGROUND: Rgb = { r: 248, g: 250, b: 252 };
/** Dark text for the light canvas. */
const DARK: Rgb = { r: 12, g: 18, b: 32 };
/** Light text for use on a dark accent fill. */
const LIGHT: Rgb = { r: 248, g: 250, b: 252 };
/** Fallback brand color when the layout has none / a malformed one — #1d4ed8. */
const FALLBACK_ACCENT: Rgb = { r: 29, g: 78, b: 216 };

/** Section-label font sizes. `md` matches the original fixed size, so a
 *  board configured before the size control looks unchanged. */
const LABEL_FONT_SIZE: Record<QmsLabelSize, string> = {
  sm: '0.875rem',
  md: '1.125rem',
  lg: '1.75rem',
  xl: '2.5rem',
};

export function buildBoardTheme(
  color: string | null | undefined,
  labelColor?: string | null,
  labelSize?: QmsLabelSize | null,
): BoardTheme {
  const accentRgb = parseHex(color) ?? FALLBACK_ACCENT;
  const accent = toHex(accentRgb);
  // The section labels take their own color; an unset / malformed value
  // falls back to the brand accent so older boards are unchanged.
  const labelAccent = toHex(parseHex(labelColor) ?? accentRgb);
  // A malformed / missing size falls back to the original `md`.
  const labelFontSize =
    LABEL_FONT_SIZE[labelSize as QmsLabelSize] ?? LABEL_FONT_SIZE.md;
  // The now-serving row fills with the accent; its text flips to light on a
  // dark brand color and dark on a light one so the code stays legible.
  const onAccent = relativeLuminance(accentRgb) < 0.5 ? LIGHT : DARK;
  return {
    background: toHex(BACKGROUND),
    foreground: toHex(DARK),
    muted: rgba(DARK, 0.55),
    surface: rgba(DARK, 0.05),
    border: rgba(DARK, 0.12),
    accent,
    labelAccent,
    labelFontSize,
    accentSoft: rgba(accentRgb, 0.14),
    highlightBackground: accent,
    highlightForeground: toHex(onAccent),
  };
}

function parseHex(value: string | null | undefined): Rgb | null {
  if (!value) return null;
  const hex = /^#?([0-9a-f]{6})$/i.exec(value.trim())?.[1];
  if (hex === undefined) return null;
  const n = parseInt(hex, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function rgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** WCAG relative luminance, 0 (black) – 1 (white). */
function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
